import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Buildings, ClipboardText, Hammer } from "@phosphor-icons/react";
import { AdminShell } from "@/components/Layout";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token")}` } });

const KIND_LABEL = {
  move_in_bulk: "Bulk Move-In",
  renovation: "Post-Renovation Punch",
  turnover_batch: "Turnover Batch",
  other: "Other",
};
const KIND_COLOR = {
  move_in_bulk: "bg-emerald-600",
  renovation: "bg-fuchsia-600",
  turnover_batch: "bg-amber-600",
  other: "bg-slate-600",
};

function CreateProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", kind: "move_in_bulk", property_address: "", unit: "",
    rooms: "Kitchen, Living Room, Master Bed, Master Bath, Bed 2, Bath 2, Exterior",
    inspectors: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Project name required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        rooms: form.rooms.split(",").map(s => s.trim()).filter(Boolean),
        inspectors: form.inspectors.split(",").map(s => s.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${BASE}/api/projects`, payload, auth());
      toast.success("Project created");
      onCreated(data);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || "Couldn't create";
      if (status === 402) toast.error(msg, { duration: 8000 });
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="project-create-modal">
      <div className="bg-white w-full max-w-lg border-2 border-[#0f172a] max-h-[95vh] overflow-y-auto">
        <div className="p-3 border-b-2 border-[#0f172a] flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase tracking-tight">New Project</div>
          <button onClick={onClose} data-testid="project-create-close" className="text-slate-500 hover:text-black">✕</button>
        </div>
        <div className="p-3 space-y-3">
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Project Name</div>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="project-name"
              placeholder="e.g. 456 Elm St Full Renovation Punch" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Kind</div>
            <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} data-testid="project-kind"
              className="w-full h-11 px-2 border-2 border-[#0f172a] text-sm bg-white">
              <option value="move_in_bulk">Bulk Move-In (multi-unit lease-up day)</option>
              <option value="renovation">Post-Renovation Punch (contractor handoff)</option>
              <option value="turnover_batch">Turnover Batch (multi-unit turn)</option>
              <option value="other">Other</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block col-span-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Property Address</div>
              <input value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})} data-testid="project-address"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            </label>
            <label className="block">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Unit (opt)</div>
              <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} data-testid="project-unit"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Rooms (comma-separated)</div>
            <input value={form.rooms} onChange={e => setForm({...form, rooms: e.target.value})} data-testid="project-rooms"
              className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Inspectors (comma-separated, opt)</div>
            <input value={form.inspectors} onChange={e => setForm({...form, inspectors: e.target.value})} data-testid="project-inspectors"
              placeholder="Andrew, Alejandro, Contractor Mike" className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          </label>
          <button onClick={submit} disabled={busy} data-testid="project-create-submit"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
            {busy ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiPill({ label, value, color }) {
  return (
    <div className={`px-2 py-1 border-2 border-[#0f172a] ${color} text-white text-[10px] font-black uppercase tracking-widest`}>
      {label}: {value}
    </div>
  );
}

export default function ProjectRadar() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/projects`, auth());
      setProjects(data.projects || []);
      setGated(false);
    } catch (e) {
      if (e.response?.status === 402) setGated(true);
      else toast.error("Couldn't load projects");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminShell>
    <div className="p-4 sm:p-6" data-testid="project-radar-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600">Enterprise</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
            <Hammer weight="fill" size={28} className="text-fuchsia-600"/> Project Radar
          </h1>
          <div className="text-sm text-slate-600">Multi-room punch lists for bulk move-ins & post-renovation walk-throughs.</div>
        </div>
        {!gated && (
          <button onClick={() => setShowCreate(true)} data-testid="project-new-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-xs tracking-widest flex items-center gap-2 border-2 border-[#0f172a]">
            <Plus weight="bold" size={16}/> New Project
          </button>
        )}
      </div>

      {gated && (
        <div className="p-6 bg-white border-2 border-fuchsia-600" data-testid="project-radar-gated">
          <div className="text-3xl">🔒</div>
          <div className="font-display font-black text-2xl mt-2 uppercase">Enterprise Feature</div>
          <div className="text-sm text-slate-700 mt-2">
            Project Radar unlocks multi-room punch lists, multi-inspector projects, and bulk kit imports —
            perfect for large-form move-ins and post-renovation walk-throughs.
          </div>
          <button onClick={() => nav("/admin/billing")} data-testid="project-radar-upgrade"
            className="mt-4 h-11 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-xs tracking-widest">
            Upgrade to Enterprise
          </button>
        </div>
      )}

      {!gated && loading && <div className="p-6 text-center font-black uppercase text-slate-400 text-xs">Loading…</div>}

      {!gated && !loading && projects.length === 0 && (
        <div className="p-6 bg-white border-2 border-dashed border-slate-300 text-center" data-testid="project-empty">
          <ClipboardText weight="bold" size={40} className="mx-auto text-slate-400"/>
          <div className="mt-2 font-black uppercase tracking-widest text-sm">No projects yet</div>
          <div className="text-xs text-slate-500 mt-1">Tap “New Project” to start a bulk move-in walkthrough or a renovation punch list.</div>
        </div>
      )}

      {!gated && !loading && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="project-list">
          {projects.map(p => (
            <button key={p.id} onClick={() => nav(`/admin/projects/${p.id}`)} data-testid={`project-card-${p.id}`}
              className="text-left p-3 bg-white border-2 border-[#0f172a] hover:shadow-[4px_4px_0px_#0f172a] transition-shadow">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white ${KIND_COLOR[p.kind] || "bg-slate-600"}`}>
                  {KIND_LABEL[p.kind] || p.kind}
                </span>
                {p.status !== "active" && (
                  <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700">{p.status}</span>
                )}
              </div>
              <div className="font-display font-black text-lg mt-1 uppercase tracking-tight">{p.name}</div>
              {p.property_address && (
                <div className="text-xs text-slate-600 mt-0.5 inline-flex items-center gap-1">
                  <Buildings size={12}/> {p.property_address}{p.unit ? ` · Unit ${p.unit}` : ""}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2" data-testid={`project-kpis-${p.id}`}>
                <KpiPill label="Open" value={p.kpis?.open || 0} color="bg-amber-600"/>
                <KpiPill label="Prog" value={p.kpis?.in_progress || 0} color="bg-blue-600"/>
                <KpiPill label="Done" value={p.kpis?.complete || 0} color="bg-emerald-600"/>
                <KpiPill label="Total" value={p.kpis?.total || 0} color="bg-slate-800"/>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); nav(`/admin/projects/${p.id}`); }}
        />
      )}
    </div>
    </AdminShell>
  );
}
